// AI秘書が作成・編集したレコードの「レビュー」共通処理。
//
// - fetchAiChanges: audit_logs（source:'ai-secretary'）から「AIがどこを変えたか」を導出する。
//   本体データに変更履歴フィールドを持たせない（単一の真実 = audit_logs）。
// - markAiReviewed: 人がレビュー確定した印として aiPendingReview を deleteField() で消す。
//   確定操作そのものも監査ログに残す。
// 詳細: docs/AI_SECRETARY_TOOL_ARCHITECTURE.md §3.5
import {
  collection,
  deleteField,
  doc,
  getDocs,
  orderBy,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '@/firebase/config';
import type { ID, Timestamp } from '@shared/types';
import { buildUpdateFields } from './firestore';
import { withAuditSource, writeAuditLog } from './audit';

/** AIが変えたフィールド1件（旧値→新値）。 */
export interface AiFieldChange {
  field: string;
  before: unknown;
  after: unknown;
}

/** AIによる操作1回ぶんの変更内容。 */
export interface AiChangeEntry {
  /** audit_logs のドキュメントID（React key 等の安定識別子）。 */
  id: string;
  action: 'create' | 'update';
  /** YYYY-MM-DD HH:mm。timestamp が無ければ null。 */
  at: string | null;
  /** create のときは空（レコード全体がAI作成）。 */
  changes: AiFieldChange[];
}

/** audit_logs ドキュメントのうち、差分導出に使う部分。 */
export interface AuditLogRow {
  id?: string;
  action?: string;
  source?: string;
  before?: unknown;
  after?: unknown;
  timestamp?: Timestamp | null;
}

/** メタ項目は差分表示の対象外（ユーザーが「AIの変更」として見たい情報ではない）。 */
const HIDDEN_FIELDS = new Set(['aiPendingReview', 'createdAt', 'updatedAt', 'createdBy', 'updatedBy']);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** 値の同一判定。Timestamp等の複合値も含め JSON 表現で比較する（表示用途には十分）。 */
function isSameValue(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

function formatAt(ts: Timestamp | null | undefined): string | null {
  if (!ts || typeof ts.toDate !== 'function') return null;
  const d = ts.toDate();
  const pad = (n: number) => `${n}`.padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * 監査ログの列（時系列）から、AIの操作ごとの差分を組み立てる純関数。
 * update の after は updateXxx に渡された「変更フィールドのみ」の形なので、
 * その各キーについて before の値と比べ、実際に変わったものだけを残す。
 *
 * 【境界】source:'ai-review-confirm'（人がレビュー確定した操作）が現れたら、
 * それ以前のエントリは破棄する ＝ パネルには「最後の確定以降」だけが出る（設計書 §3.5）。
 */
export function computeAiChanges(logs: AuditLogRow[]): AiChangeEntry[] {
  let entries: AiChangeEntry[] = [];
  for (const log of logs) {
    if (log.source === 'ai-review-confirm') {
      entries = []; // ここまでの変更は人がレビュー済み
      continue;
    }
    const at = formatAt(log.timestamp);
    if (log.action === 'create') {
      entries.push({ id: log.id ?? '', action: 'create', at, changes: [] });
      continue;
    }
    if (log.action !== 'update') continue; // delete/read は表示対象外

    const afterObj = isRecord(log.after) ? log.after : {};
    const beforeObj = isRecord(log.before) ? log.before : {};
    const changes = Object.entries(afterObj)
      .filter(([field]) => !HIDDEN_FIELDS.has(field))
      .filter(([field, afterValue]) => !isSameValue(beforeObj[field], afterValue))
      .map(([field, afterValue]): AiFieldChange => ({
        field,
        before: field in beforeObj ? beforeObj[field] : null,
        after: afterValue,
      }));
    if (changes.length > 0) entries.push({ id: log.id ?? '', action: 'update', at, changes });
  }
  return entries;
}

/**
 * 対象レコードに対するAI由来の変更履歴（＋レビュー確定の境界ログ）を取得する。
 * 一般ユーザーは firestore.rules 上 source を絞ったクエリしか許可されないため、
 * この where('source' in) は権限面でも必須（外すと権限エラーになる）。
 */
export async function fetchAiChanges(entityType: string, entityId: ID): Promise<AiChangeEntry[]> {
  const q = query(
    collection(db, 'audit_logs'),
    where('entityType', '==', entityType),
    where('entityId', '==', entityId),
    where('source', 'in', ['ai-secretary', 'ai-review-confirm']),
    orderBy('timestamp', 'asc')
  );
  const snap = await getDocs(q);
  return computeAiChanges(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<AuditLogRow, 'id'>) })));
}

/**
 * レビュー確定: aiPendingReview をフィールドごと削除し、確定操作を監査ログに残す。
 * 確定ログには source:'ai-review-confirm' を刻む。これが変更箇所パネルの「境界」になり、
 * 次にAIが同じレコードを編集したとき、確定以前の履歴は表示されない（設計書 §3.5）。
 * 「AIが作った」事実は audit_logs の source:'ai-secretary' に残り続ける。
 */
export async function markAiReviewed(
  collectionName: string,
  entityType: string,
  id: ID,
  userId: ID
): Promise<void> {
  await updateDoc(doc(db, collectionName, id), {
    aiPendingReview: deleteField(),
    ...buildUpdateFields(userId),
  });
  await withAuditSource('ai-review-confirm', () =>
    writeAuditLog({
      entityType,
      entityId: id,
      action: 'update',
      userId,
      before: { aiPendingReview: true },
      after: { aiPendingReview: null },
    })
  );
}
